import { useQuery } from "@apollo/client";
import NewEndorsement from "components/Modal/Endorsement/NewEndorsement";
import { useDrawer } from "context/drawer/drawer.provider";
import { gqlMessage } from "gql";
import { CircleCheckBig } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { IMessage } from "types/Message.type";
import EndorsementModal from "../EndorsementModal";
import EndorsementItem from "../SideCard/EndorsementItem";
import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

interface EndorsementWidgetProps {
  readonly variant?: "default" | "v2";
}

// * Max ammount of messages to show on widget
const maxQty = 5;

const EndorsementWidget = (props: EndorsementWidgetProps) => {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const { openDrawer } = useDrawer();

  const [messages, setMessages] = useState<IMessage[]>([]);
  const { endorsements, setEndorsements, hasHydrated } =
    useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(endorsements.lastFetched);

  const { loading: loadingMessages } = useQuery<{
    getEndorsements: IMessage[];
  }>(gqlMessage.queries.GET_ENDORSEMENTS, {
    fetchPolicy: "network-only",
    skip: !hasHydrated || hasValidCache,
    onCompleted: ({ getEndorsements }) => {
      setEndorsements(getEndorsements || []);
    },
    onError: (error) => {
      console.error("🚀 ~ error", error);
    }
  });

  useEffect(() => {
    if (endorsements.data) {
      setMessages(endorsements.data);
    }
  }, [endorsements.data]);

  const handleEndorsement = (message: IMessage) => {
    openDrawer({
      drawerComponent: (
        <NewEndorsement
          skillId={message?.skill?.id || ""}
          userId={message?.createdBy?.id || ""}
          name={message?.skill?.name || ""}
        />
      )
    });
  };

  const renderItems = () =>
    messages.slice(0, maxQty).map((message) => (
      <div
        className={variant === "v2" ? "my-1" : "my-2"}
        key={`${message.id}_endorsement_wdgt`}
      >
        <EndorsementItem
          message={message}
          onEndorse={handleEndorsement}
          compact={variant === "v2"}
        />
      </div>
    ));

  const handleViewMore = () => {
    openDrawer({
      drawerComponent: <EndorsementModal messages={messages} />
    });
  };

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  // No mostrar la card si no hay validaciones.
  if (!loadingMessages && messages.length === 0) {
    return null;
  }

  return (
    <ActiveSideCard
      title={t("Validaciones")}
      loading={loadingMessages && !endorsements.data}
      buttonText={messages.length > maxQty ? t("Ver todos") : ""}
      collapsible={variant === "default" ? true : false}
      onViewMore={handleViewMore}
      variant={variant}
      icon={
        variant === "v2" ? (
          <CircleCheckBig size={16} strokeWidth={1} color="#0073FF" />
        ) : null
      }
    >
      <div>
        <span className={variant === "v2" ? "text-[11px]" : "text-xs"}>
          {t("Ayuda a tus colaboradores validando sus habilidades")}
        </span>

        {renderItems()}
      </div>
    </ActiveSideCard>
  );
};

export default EndorsementWidget;
